import { useEffect, useRef } from "react";
import {
  Accessibility,
  Baby,
  ChevronDown,
  ChevronRight,
  Clock3,
  Navigation,
  ShieldCheck,
} from "lucide-react";
import type { CandidateSortMode } from "../candidateSorting";
import { getListCandidateEntries } from "../candidateVisibility";
import { getArrivalConfidence } from "../arrivalConfidence";
import { featureMeta, formatDistance, stateMeta } from "../format";
import type { EvaluatedToilet, FeatureKey } from "../types";
import { StatusBadge } from "./StatusBadge";

const MOBILE_LAYOUT_QUERY = "(max-width: 820px)";
export const NEARBY_RADIUS_METERS = 2_000;

export const countNearbyCandidates = (
  items: readonly Pick<EvaluatedToilet, "distanceMeters">[],
) =>
  items.reduce(
    (count, item) =>
      item.distanceMeters <= NEARBY_RADIUS_METERS ? count + 1 : count,
    0,
  );

const facilityNameSegmenter =
  "Segmenter" in Intl
    ? new Intl.Segmenter("ja", { granularity: "word" })
    : undefined;

const segmentFacilityName = (name: string) =>
  facilityNameSegmenter
    ? Array.from(facilityNameSegmenter.segment(name), ({ segment }) => segment)
    : Array.from(name);

const FeatureIcon = ({ feature }: { feature: FeatureKey }) => {
  if (feature === "wheelchair") return <Accessibility aria-hidden="true" />;
  if (feature === "baby_changing") return <Baby aria-hidden="true" />;
  return <ShieldCheck aria-hidden="true" />;
};

export function CandidateList({
  items,
  selectedId,
  sortMode,
  onSortChange,
  onSelect,
  onDetail,
  onNavigate,
  navigationEnabled = true,
  originReady = true,
  onRequestOrigin,
  filtersActive = false,
  onClearFilters,
  revealRequest,
  exactRouteIds = new Set<string>(),
  evaluationTime = new Date(),
  nearbyCount,
  embedded = false,
  autoScrollOnMobile = false,
}: {
  items: EvaluatedToilet[];
  selectedId?: string;
  sortMode: CandidateSortMode;
  onSortChange: (mode: CandidateSortMode) => void;
  onSelect: (id: string) => void;
  onDetail: (item: EvaluatedToilet) => void;
  onNavigate: (item: EvaluatedToilet) => void;
  navigationEnabled?: boolean;
  originReady?: boolean;
  onRequestOrigin?: () => void;
  filtersActive?: boolean;
  onClearFilters?: () => void;
  revealRequest?: { id: string; sequence: number };
  exactRouteIds?: ReadonlySet<string>;
  evaluationTime?: Date;
  nearbyCount?: number;
  embedded?: boolean;
  autoScrollOnMobile?: boolean;
}) {
  const rowRefs = useRef(new Map<string, HTMLElement>());
  const listRef = useRef<HTMLDivElement>(null);
  const listEntries = getListCandidateEntries(items, selectedId);
  const revealId = revealRequest?.id;
  const revealSequence = revealRequest?.sequence;
  const revealedEntry = revealId
    ? listEntries.find(({ item }) => item.id === revealId)
    : undefined;
  const nearbyItemCount =
    nearbyCount ?? countNearbyCandidates(items);
  const sortExplanation =
    sortMode === "distance"
      ? "出発地からの直線距離が近い順です。徒歩時間は目安です。"
      : sortMode === "freshness"
        ? "利用できた確認が新しい順です。現在有効な利用確認がない候補は後に表示します。"
        : "距離だけでなく、利用状態・混雑・情報鮮度を加味しています。";

  useEffect(() => {
    if (!revealId || revealSequence === undefined) return;
    if (
      window.matchMedia?.(MOBILE_LAYOUT_QUERY).matches &&
      !autoScrollOnMobile
    ) {
      return;
    }
    const frame = window.requestAnimationFrame(() => {
      const row = rowRefs.current.get(revealId);
      const list = listRef.current;
      if (!row || !list) return;
      const reducedMotion =
        window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
      const listRect = list.getBoundingClientRect();
      const rowRect = row.getBoundingClientRect();
      list.scrollTo?.({
        top: Math.max(0, list.scrollTop + rowRect.top - listRect.top - 12),
        behavior: reducedMotion ? "auto" : "smooth",
      });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [autoScrollOnMobile, revealId, revealSequence]);

  return (
    <section
      className={`recommendations${embedded ? " is-embedded" : ""}`}
      aria-labelledby="recommendation-heading"
    >
      {embedded ? (
        <h2 id="recommendation-heading" className="sr-only" tabIndex={-1}>
          トイレ候補
        </h2>
      ) : null}
      <div className="recommendation-heading">
        {!embedded ? (
          <div className="recommendation-title">
            <h2 id="recommendation-heading" tabIndex={-1}>
              トイレ候補
            </h2>
            <span
              className="candidate-radius-count"
              role="status"
              aria-live="polite"
              aria-label={`出発地から半径2キロ以内に${nearbyItemCount}件`}
            >
              <small aria-hidden="true">半径2km以内</small>
              <strong aria-hidden="true">{nearbyItemCount}件</strong>
            </span>
          </div>
        ) : null}
        <label className="candidate-sort" htmlFor="candidate-sort">
          <span>表示順</span>
          <span className="candidate-sort-control">
            <select
              id="candidate-sort"
              value={sortMode}
              aria-controls="candidate-list"
              aria-describedby="candidate-sort-description"
              onChange={(event) => {
                listRef.current?.scrollTo?.({ top: 0 });
                onSortChange(event.target.value as CandidateSortMode);
              }}
            >
              <option value="recommended">おすすめ順</option>
              <option value="distance">距離が近い順</option>
              <option value="freshness">利用確認が新しい順</option>
            </select>
            <ChevronDown aria-hidden="true" focusable="false" />
          </span>
        </label>
      </div>
      <div
        className="ranking-explanation"
        id="candidate-sort-description"
        role="status"
        aria-live="polite"
      >
        <ShieldCheck aria-hidden="true" />
        {sortExplanation}
      </div>
      <div className="candidate-list" id="candidate-list" ref={listRef}>
        <span className="sr-only" role="status" aria-live="polite">
          {revealedEntry
            ? `${revealedEntry.rank}番候補 ${revealedEntry.item.name}を一覧に表示しました`
            : ""}
        </span>
        {items.length === 0 && (
          <div className="empty-state" role="status">
            <b>条件に合う候補が見つかりません</b>
            <span>設備フィルターを減らすか、検索基準点を変更してください。</span>
            {filtersActive && onClearFilters && (
              <button
                className="secondary-button empty-state-reset"
                type="button"
                onClick={onClearFilters}
              >
                条件をすべて解除
              </button>
            )}
          </div>
        )}
        {listEntries.map(({ item, rank }) => {
          const hasExactRoute = exactRouteIds.has(item.id);
          const arrivalConfidence = getArrivalConfidence(item, evaluationTime);
          const routeBlockedByState =
            item.state === "likely_unavailable" ||
            item.state === "unavailable_confirmed";
          const enabledFeatures = (
            Object.entries(item.features) as [FeatureKey, boolean | null][]
          )
            .filter(([, value]) => value === true)
            .slice(0, 3);
          return (
            <article
              className={`candidate-row ${selectedId === item.id ? "is-selected" : ""}`}
              key={item.id}
              data-candidate-id={item.id}
              ref={(node) => {
                if (node) rowRefs.current.set(item.id, node);
                else rowRefs.current.delete(item.id);
              }}
            >
              <button
                className="candidate-main"
                type="button"
                onClick={() => onSelect(item.id)}
                aria-label={`${rank}番候補 ${item.name}、${stateMeta[item.state].label}、${
                  originReady
                    ? `${hasExactRoute ? "到着" : "徒歩目安"}${item.walkingMinutes}分`
                    : `エリア中心から${formatDistance(item.distanceMeters)}`
                }、${arrivalConfidence.label}、${item.freshnessLabel}。この候補を選択して地図へ移動`}
                aria-current={selectedId === item.id ? "true" : undefined}
              >
                <span className="candidate-rank">{rank}</span>
                <span className="candidate-copy">
                  <StatusBadge state={item.state} />
                  <b className="candidate-name">
                    {segmentFacilityName(item.name).map((segment, index) => (
                      <span key={`${index}-${segment}`}>{segment}</span>
                    ))}
                  </b>
                  <span className="journey">
                    <span className="journey-primary">
                      {originReady ? (
                        <Navigation aria-hidden="true" />
                      ) : (
                        <Clock3 aria-hidden="true" />
                      )}
                      {originReady
                        ? hasExactRoute
                          ? `到着 約${item.walkingMinutes}分`
                          : `徒歩目安 ${item.walkingMinutes}分`
                        : "エリア中心から"}
                    </span>
                    <i>・{formatDistance(item.distanceMeters)}</i>
                  </span>
                  <span
                    className={`candidate-reason is-${arrivalConfidence.tone}`}
                  >
                    {arrivalConfidence.label}
                  </span>
                  <span className="feature-list">
                    {enabledFeatures.map(([feature]) => (
                      <i key={feature}>
                        <FeatureIcon feature={feature} />
                        {featureMeta[feature]}
                      </i>
                    ))}
                  </span>
                </span>
                <span className="candidate-freshness">
                  <Clock3 aria-hidden="true" />
                  {item.freshnessLabel}
                </span>
              </button>
              <div className="candidate-actions">
                <button
                  type="button"
                  onClick={() =>
                    originReady ? onNavigate(item) : onRequestOrigin?.()
                  }
                  disabled={
                    routeBlockedByState ||
                    (originReady && !navigationEnabled) ||
                    (!originReady && !onRequestOrigin)
                  }
                  aria-label={
                    routeBlockedByState
                      ? `${item.name}は利用困難の情報があるため案内できません`
                      : !originReady
                        ? `${item.name}への案内に使う出発地を設定`
                        : navigationEnabled
                          ? `${item.name}までの徒歩経路を地図に表示`
                          : `${item.name}までの徒歩経路は現在利用できません`
                  }
                >
                  {originReady ? (
                    <Navigation aria-hidden="true" />
                  ) : (
                    <Clock3 aria-hidden="true" />
                  )}
                  {originReady ? "案内" : "出発地"}
                </button>
                <button
                  type="button"
                  onClick={() => onDetail(item)}
                  aria-label={`${item.name}の詳細を表示`}
                >
                  詳細
                  <ChevronRight aria-hidden="true" />
                </button>
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}
